/**
 * SA-459 (workflow UX sweep spec §3.2, D2): what colour a step is. ONE
 * helper, read by every coloured surface — the map card, the list rows, the
 * drawer chips, the timeline nodes and the legend — so the four role hues
 * (`--role-*`, styles/globals.css) are applied by one rule. Pure: no DOM,
 * no store.
 */
import type { CSSProperties } from 'react'
import type { ProcessStepVM } from '@/utils/workflowOverview'

export type Hue = 'person' | 'ai' | 'automation' | 'conversation'

export interface ExecutorTone {
  /** The stripe, top to bottom: one hue, or [ai, person] on an assisted person step. */
  stripe: Hue[]
  /** The hue for a dot, a chip, a timeline node — the stripe's first hue. */
  hue: Hue
  /** `bg-role-<hue>/12 text-role-<hue>`: a chip's wash and ink. */
  chip: string
  /** `bg-role-<hue>`: a dot or a swatch. */
  dot: string
  /** `text-role-<hue>`. */
  text: string
  /** The stripe as a CSS background (a 50/50 linear-gradient for two hues). */
  stripeStyle: CSSProperties
}

const CHIP: Record<Hue, string> = {
  person: 'bg-role-person/12 text-role-person',
  ai: 'bg-role-ai/12 text-role-ai',
  automation: 'bg-role-automation/12 text-role-automation',
  conversation: 'bg-role-conversation/12 text-role-conversation',
}
const DOT: Record<Hue, string> = {
  person: 'bg-role-person',
  ai: 'bg-role-ai',
  automation: 'bg-role-automation',
  conversation: 'bg-role-conversation',
}
const TEXT: Record<Hue, string> = {
  person: 'text-role-person',
  ai: 'text-role-ai',
  automation: 'text-role-automation',
  conversation: 'text-role-conversation',
}

export function hueClasses(hue: Hue): { chip: string; dot: string; text: string } {
  return { chip: CHIP[hue], dot: DOT[hue], text: TEXT[hue] }
}

/**
 * The wireframe's `stripeColors()`: a person step shows the AI above the
 * person at assisted and the AI alone at automatic; an `ai` executor shows
 * the AI above the person at assisted (the AI proposes, a person confirms —
 * its chips say so) and is violet alone at manual (declared, not turned on)
 * and at automatic; a wait shares the automation hue.
 */
export function stripeHues(executorType: ProcessStepVM['executorType'], level: string | null): Hue[] {
  switch (executorType) {
    case 'person':
      return level === 'assisted' ? ['ai', 'person'] : level === 'automatic' ? ['ai'] : ['person']
    case 'ai':
      return level === 'assisted' ? ['ai', 'person'] : ['ai']
    case 'automation':
    case 'wait':
      return ['automation']
    case 'conversation':
      return ['conversation']
  }
}

/** The stripe as a background: one hue, or a 50/50 gradient of two. */
export function stripeStyle(stripe: Hue[]): CSSProperties {
  return stripe.length === 1
    ? { background: `var(--role-${stripe[0]})` }
    : { background: `linear-gradient(var(--role-${stripe[0]}) 50%, var(--role-${stripe[1]}) 50%)` }
}

export function executorTone(step: Pick<ProcessStepVM, 'executorType' | 'level'>): ExecutorTone {
  const stripe = stripeHues(step.executorType, step.level)
  const hue = stripe[0]
  return { stripe, hue, ...hueClasses(hue), stripeStyle: stripeStyle(stripe) }
}

export interface LegendEntry {
  hue: Hue | 'assisted'
  label: string
  style: CSSProperties
}

/** The legend under the map (spec §4.3), in the wireframe's order. */
export function legendEntries(): LegendEntry[] {
  return [
    { hue: 'person', label: 'Person', style: stripeStyle(['person']) },
    { hue: 'ai', label: 'AI', style: stripeStyle(['ai']) },
    { hue: 'automation', label: 'Automation, and a wait', style: stripeStyle(['automation']) },
    { hue: 'conversation', label: 'Intake / conversation', style: stripeStyle(['conversation']) },
    { hue: 'assisted', label: 'Assisted: the AI proposes, a person confirms', style: stripeStyle(['ai', 'person']) },
  ]
}

export interface LineLegendEntry {
  kind: 'next' | 'outcome' | 'revision'
  label: string
  /** The SVG `stroke-dasharray`, null for a solid line. */
  dash: string | null
  /** Drawn in the warning hue (a revision path). */
  warning: boolean
}

/** The one dash pattern per line kind, read by the legend and the draw phase alike. */
export function lineDash(kind: LineLegendEntry['kind']): string | undefined {
  return lineLegendEntries().find((e) => e.kind === kind)?.dash ?? undefined
}

/** SA-1077 (UX pass spec move 3): the three line kinds the map draws, under the hues. */
export function lineLegendEntries(): LineLegendEntry[] {
  return [
    { kind: 'next', label: 'Next step', dash: null, warning: false },
    { kind: 'outcome', label: 'Outcome', dash: '4 3', warning: false },
    { kind: 'revision', label: 'Revision path', dash: '2 3', warning: true },
  ]
}
